import { useCallback } from "react";
import { useEncryption, base64ToUint8Array } from "~/hooks/useEncryption";
import { unwrapDekFromUser } from "~/helpers/envelopeCrypto";
import { CRYPTO_SERVICE, CryptoResult } from "~/workers/crypto";

/**
 * Retorna uma factory que cria um decryptor consciente de envelope encryption.
 *
 * Se o item tiver `user_wrapped_dek`, desembrulha a DEK com a chave pessoal do usuário
 * e usa essa DEK para descriptografar os campos. Caso contrário, usa o decryptor legado
 * (chave pessoal diretamente).
 *
 * Pode ser usado em qualquer componente que precise descriptografar relatórios individualmente,
 * como downloads de PDF/DOCX no ReportsList.
 */
export function useEnvelopeAwareDecryptor() {
  const { decryptData, deriveKey } = useEncryption();

  const createDecryptorForItem = useCallback(
    (item: { user_wrapped_dek?: string | null; user_dek_iv?: string | null }) => {
      if (!item.user_wrapped_dek || !item.user_dek_iv) {
        return decryptData;
      }

      let cachedDek: Uint8Array | null = null;

      return async (encryptedField: { encrypted: string; iv: string }): Promise<CryptoResult<any>> => {
        try {
          if (!cachedDek) {
            const userDek = await deriveKey();
            const rawKey = userDek.buffer.slice(
              userDek.byteOffset,
              userDek.byteOffset + userDek.byteLength,
            ) as ArrayBuffer;
            cachedDek = await unwrapDekFromUser(rawKey, item.user_wrapped_dek!, item.user_dek_iv!);
          }

          const ciphertext = base64ToUint8Array(encryptedField.encrypted);
          const iv = base64ToUint8Array(encryptedField.iv);
          const { decrypted } = await CRYPTO_SERVICE.decrypt(cachedDek, iv, ciphertext);
          return { success: true, data: decrypted };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : "Erro na descriptografia envelope",
          };
        }
      };
    },
    [decryptData, deriveKey],
  );

  return { createDecryptorForItem };
}
